/// Nautical Almanac arithmetic — the "GHA / Dec corrections" workflow
/// the RYA Ocean syllabus drills.
///
/// The Almanac prints, for each whole hour UT, the Greenwich Hour
/// Angle and Declination of every navigational body. To get the
/// values for the actual moment of the sight you apply two
/// arithmetic corrections:
///
///   GHA(h:m:s) = GHA(h:00:00)
///              + increment(body, m, s)        — minutes/seconds at the body's nominal rate
///              + v_correction(v, m, s)        — body's deviation from nominal rate
///
///   Dec(h:m:s) = Dec(h:00:00)
///              + d_correction(d, m, s)        — Dec change rate
///
/// The nominal rates are the constants the "Increments and Corrections"
/// yellow pages of the Nautical Almanac assume:
///
///   Sun, planets:  15.0°  per hour
///   Aries (stars): 15.04107° per hour (sidereal rate)
///   Moon:          14.31667° per hour
///
/// The v and d columns on the daily pages absorb the small extra: for
/// the Sun and Aries the printed hourly values are pre-adjusted so v
/// is always 0; for planets v is a few arc-min/hr; for the Moon v can
/// be ±15 arc-min/hr.
///
/// We expose two helpers:
///
///   - The pure arithmetic (`applyGhaIncrement`, `applyDecIncrement`)
///     — what the student writes in the pro-forma worksheet.
///   - A simulated almanac extract (`almanacExtract`) — picks plausible
///     hourly GHA/Dec/v/d values from the underlying ephemeris so the
///     drill can hand the student a realistic problem without us
///     shipping the printed Almanac itself.

import { normalize360 } from "./time";
import { sunPosition } from "./sun";
import { moonPosition } from "./moon";
import { planetPosition, type Planet } from "./planets";

/// Bodies that the Almanac tabulates with their own daily-page column.
/// "aries" is the GHA-of-Aries column used for star sights, not a
/// physical body.
export type AlmanacBody = "sun" | "aries" | "moon" | Planet;

/// Nominal hourly rate of GHA, degrees per hour. These are the
/// constants the "Increments and Corrections" yellow pages assume.
export const NOMINAL_HOURLY_RATE_DEG: Record<AlmanacBody, number> = {
  sun: 15.0,
  aries: 15.04107,
  venus: 15.0,
  mars: 15.0,
  jupiter: 15.0,
  saturn: 15.0,
  moon: 14.31667,
};

/// GHA of Aries (the First Point of Aries) — the celestial reference
/// for star sights. `GHA(*) = GHA(Aries) + SHA(*)`.
export function ghaAriesDeg(date: Date): number {
  return normalize360(sunPosition(date).gmstHours * 15);
}

/// Increment to add to the hourly GHA for `m` minutes and `s` seconds
/// past the hour, in arc-minutes (the form the Almanac tabulates).
export function ghaIncrementMin(
  body: AlmanacBody,
  m: number,
  s: number,
): number {
  const hours = m / 60 + s / 3600;
  return NOMINAL_HOURLY_RATE_DEG[body] * 60 * hours;
}

/// v-correction in arc-minutes. `vPerHourMin` is the v value listed
/// on the daily page (arc-minutes per hour); the partial-hour
/// correction is simply `v × (m/60 + s/3600)`. For Sun and Aries v = 0.
export function vCorrectionMin(
  vPerHourMin: number,
  m: number,
  s: number,
): number {
  return vPerHourMin * (m / 60 + s / 3600);
}

/// d-correction in arc-minutes. `dPerHourMin` is the d value from the
/// daily page (arc-minutes per hour); the partial-hour correction is
/// `d × (m/60 + s/3600)`. Signed (positive = Dec increasing).
export function dCorrectionMin(
  dPerHourMin: number,
  m: number,
  s: number,
): number {
  return dPerHourMin * (m / 60 + s / 3600);
}

/// Apply the full Almanac arithmetic for GHA: hourly GHA + increment
/// + v-correction, normalised to [0, 360°).
export function applyGhaIncrement(input: {
  body: AlmanacBody;
  hourlyGhaDeg: number;
  m: number;
  s: number;
  /// v from the daily page, arc-min/hr. 0 for Sun / Aries.
  vPerHourMin: number;
}): number {
  const inc = ghaIncrementMin(input.body, input.m, input.s);
  const v = vCorrectionMin(input.vPerHourMin, input.m, input.s);
  return normalize360(input.hourlyGhaDeg + (inc + v) / 60);
}

/// Apply the full Almanac arithmetic for Dec: hourly Dec + d-correction.
/// Sign of d follows whether Dec is moving N (+) or S (−).
export function applyDecIncrement(input: {
  hourlyDecDeg: number;
  m: number;
  s: number;
  /// d from the daily page, arc-min/hr. Signed.
  dPerHourMin: number;
}): number {
  const d = dCorrectionMin(input.dPerHourMin, input.m, input.s);
  return input.hourlyDecDeg + d / 60;
}

/// Simulated daily-page extract for a body at the start of the given
/// hour. The v / d values are derived from how the body's actual GHA
/// and Dec change over the next hour, matching the convention the
/// real Almanac uses.
export interface AlmanacExtract {
  /// The hour bucket the extract applies to (UT, minutes/seconds zeroed).
  hour: Date;
  /// Hourly GHA, degrees.
  ghaDeg: number;
  /// Hourly Declination, degrees (signed).
  decDeg: number;
  /// v from the daily page, arc-minutes per hour. 0 for Sun and Aries.
  vPerHourMin: number;
  /// d from the daily page, arc-minutes per hour. Signed.
  dPerHourMin: number;
}

export function almanacExtract(body: AlmanacBody, hour: Date): AlmanacExtract {
  // Round to the start of the UT hour.
  const startHour = new Date(hour);
  startHour.setUTCMinutes(0, 0, 0);
  const nextHour = new Date(startHour.getTime() + 3_600_000);

  const at = ghaDecAt(body, startHour);
  const after = ghaDecAt(body, nextHour);

  // GHA difference over the hour, normalised to (−180, +180].
  let dGhaDeg = after.ghaDeg - at.ghaDeg;
  while (dGhaDeg <= -180) dGhaDeg += 360;
  while (dGhaDeg > 180) dGhaDeg -= 360;

  // v-correction = actual rate minus nominal rate, in arc-min/hr.
  // For Sun and Aries the Almanac prints adjusted hourly values so
  // the increment table is exactly nominal — match that here by
  // forcing v = 0.
  const vPerHourMin =
    body === "sun" || body === "aries"
      ? 0
      : (dGhaDeg - NOMINAL_HOURLY_RATE_DEG[body]) * 60;
  const dPerHourMin = (after.decDeg - at.decDeg) * 60;

  return {
    hour: startHour,
    ghaDeg: at.ghaDeg,
    decDeg: at.decDeg,
    vPerHourMin,
    dPerHourMin,
  };
}

function ghaDecAt(
  body: AlmanacBody,
  date: Date,
): { ghaDeg: number; decDeg: number } {
  if (body === "sun") {
    const s = sunPosition(date);
    return { ghaDeg: s.ghaDeg, decDeg: s.decDeg };
  }
  if (body === "moon") {
    const m = moonPosition(date);
    return { ghaDeg: m.ghaDeg, decDeg: m.decDeg };
  }
  if (body === "aries") {
    return { ghaDeg: ghaAriesDeg(date), decDeg: 0 };
  }
  const p = planetPosition(body, date);
  return { ghaDeg: p.ghaDeg, decDeg: p.decDeg };
}
